import type { UserSettings, DailyProgress, ProblemType } from '../types';
import { STORAGE_KEYS, STAGE_ORDER } from '../constants';

export function getTodayString(): string {
  return new Date().toISOString().split('T')[0];
}

export function getDefaultSettings(): UserSettings {
  return {
    enabledTypes: STAGE_ORDER.reduce(
      (acc, key) => ({ ...acc, [key]: key === STAGE_ORDER[0] }),
      {} as Record<ProblemType, boolean>
    ),
  };
}

export function getDefaultDailyProgress(): DailyProgress {
  return {
    wins: {},
    lastPlayed: getTodayString(),
  };
}

export function loadSettings(): UserSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    if (stored) {
      const parsed = JSON.parse(stored) as UserSettings;
      const defaults = getDefaultSettings();
      const merged = { ...defaults.enabledTypes, ...parsed.enabledTypes };

      // Migration: when a new stage is inserted into STAGE_ORDER, existing users
      // have no setting for it (so it defaults to off and never appears). Auto-
      // unlock such a NEW stage if the previous stage is already unlocked — i.e.
      // the learner has progressed past where it sits. Manual on/off choices for
      // existing stages are left untouched.
      const storedKeys = new Set(Object.keys(parsed.enabledTypes ?? {}));
      STAGE_ORDER.forEach((type, i) => {
        if (!storedKeys.has(type) && i > 0 && merged[STAGE_ORDER[i - 1]]) {
          merged[type] = true;
        }
      });

      return { enabledTypes: merged };
    }
  } catch (e) {
    console.error('Failed to load settings:', e);
  }
  return getDefaultSettings();
}

export function saveSettings(settings: UserSettings): void {
  try {
    localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
  } catch (e) {
    console.error('Failed to save settings:', e);
  }
}

export function loadDailyProgress(): DailyProgress {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.DAILY_PROGRESS);
    if (stored) {
      const parsed = JSON.parse(stored) as DailyProgress;
      const today = getTodayString();
      if (parsed.lastPlayed !== today) {
        const fresh = getDefaultDailyProgress();
        saveDailyProgress(fresh);
        return fresh;
      }
      return parsed;
    }
  } catch (e) {
    console.error('Failed to load daily progress:', e);
  }
  return getDefaultDailyProgress();
}

export function saveDailyProgress(progress: DailyProgress): void {
  try {
    localStorage.setItem(STORAGE_KEYS.DAILY_PROGRESS, JSON.stringify(progress));
  } catch (e) {
    console.error('Failed to save daily progress:', e);
  }
}

export function incrementWin(category: ProblemType): DailyProgress {
  const progress = loadDailyProgress();
  const currentWins = progress.wins[category] || 0;
  const updated: DailyProgress = {
    ...progress,
    wins: {
      ...progress.wins,
      [category]: currentWins + 1,
    },
    lastPlayed: getTodayString(),
  };
  saveDailyProgress(updated);
  return updated;
}
